import { FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/form-control';
import { Input } from '@chakra-ui/input';
import { Box, Link, VStack } from '@chakra-ui/layout';
import { Button, Flex, Select } from '@chakra-ui/react';
import { US_STATES } from '@common/constants';
import maps from '@common/maps';
import { zipCodeRegExUs } from '@common/utility/validations';
import React, { FormEvent, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextBlock } from '../../common/components/TextBlock';
import { ROUTES } from '../../config';
import { CreateAccountForm } from '../types';
import { PageHeader } from './components/PageHeader';
import { UI } from './components/UI';

const selectAddress = async (form: UseFormReturn<CreateAccountForm>, place: google.maps.places.PlaceResult) => {
  let address1 = '';
  let state = '';
  let city = '';
  let zip = '';

  if (!place.address_components) return;

  place.address_components.forEach((item: google.maps.GeocoderAddressComponent) => {
    switch (item.types[0]) {
      case 'postal_code':
        zip = item.long_name;
        form?.setValue('zip', zip);
        break;
      case 'administrative_area_level_1':
        state = item.short_name;
        form?.setValue('state', state);
        break;
      case 'street_number':
        address1 = address1 + item.long_name;
        break;
      case 'route':
        address1 = address1 + ' ' + item.long_name;
        break;
      default:
        if (item.types.some((value) => value === 'locality') || item.types.some((value) => value === 'sublocality')) {
          city = item.long_name;
          form?.setValue('city', city);
        }
        break;
    }
  });
  if (address1 !== '') {
    form?.setValue('address1', address1);
  }

  return await form.trigger();
};

type AddressState = 'DISPLAY' | 'MANUAL' | 'SEARCH';

type AddressProps = {
  form: UseFormReturn<CreateAccountForm>;
};

export const Address: React.FC<AddressProps> = ({ form }) => {
  const { address1, city, state, zip } = form.watch();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const redirectAsParam = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';

  let initialState: AddressState = 'SEARCH';

  if (address1 && city && state && zip) {
    initialState = 'DISPLAY';
  }

  const [addressState, setAddressState] = useState<AddressState>(initialState);

  const navigate = useNavigate();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const valid = await form.trigger();
    if (valid) {
      navigate(ROUTES.createReviewCreateAccount + redirectAsParam);
    }
  };

  useEffect(() => {
    maps.loadPlaces('#autocomplete', async (place) => {
      if (!place?.address_components) return;
      await selectAddress(form, place);
      form.trigger();
      setAddressState('DISPLAY');
    });
  }, [addressState]);

  let content;
  switch (addressState) {
    case 'SEARCH':
      content = null;
      break;
    case 'DISPLAY':
      content = (
        <Box borderColor={'gray.100'} borderWidth={'1px'} p={'14px'}>
          <Input
            style={{ caretColor: 'transparent' }}
            outlineColor={'transparent'}
            borderWidth={0}
            _focusVisible={{ borderColor: 'transparent' }}
            _focus={{ outline: 'transparent' }}
            _active={{ outline: 'transparent' }}
            value={address1}
            onChange={(e) => e.preventDefault()}></Input>
          <Input
            style={{ caretColor: 'transparent' }}
            outlineColor={'transparent'}
            borderWidth={0}
            _focusVisible={{ borderColor: 'transparent' }}
            _focus={{ outline: 'transparent' }}
            _active={{ outline: 'transparent' }}
            value={`${city}, ${state} ${zip}`}
            onChange={(e) => e.preventDefault()}
            autoFocus></Input>
        </Box>
      );
      break;
    case 'MANUAL':
      content = (
        <VStack gap={'10px'} width="xl" maxW={'100%'} margin={'auto'} overflow={'auto'}>
          <FormControl isInvalid={Boolean(form.formState.errors.address1)}>
            <FormLabel htmlFor="address1">Address 1</FormLabel>
            <Input
              id="address1"
              placeholder="Address 1"
              {...form.register('address1', {
                required: 'Please enter a street address',
              })}
            />
            <FormErrorMessage>
              {form.formState.errors.address1 && form.formState.errors.address1.message}
            </FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(form.formState.errors.address2)}>
            <FormLabel htmlFor="address2">Address 2</FormLabel>
            <Input id="address2" mb={3} {...form.register('address2', {})} />
          </FormControl>
          <FormControl isInvalid={Boolean(form.formState.errors.city)}>
            <FormLabel htmlFor="city">City</FormLabel>
            <Input
              id="city"
              mb={3}
              placeholder="City"
              {...form.register('city', {
                required: 'Please enter a city',
              })}
            />
            <FormErrorMessage>{form.formState.errors.city && form.formState.errors.city.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(form.formState.errors.state)}>
            <FormLabel htmlFor="state">State</FormLabel>
            <Select
              mb={3}
              placeholder="Select option"
              {...form.register('state', {
                required: 'Please enter a state',
              })}>
              {Object.keys(US_STATES).map((key) => {
                return (
                  <option key={`state-${key}`} value={key}>
                    {US_STATES[key]}
                  </option>
                );
              })}
            </Select>
            <FormErrorMessage>{form.formState.errors.state && form.formState.errors.state.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(form.formState.errors.zip)}>
            <FormLabel htmlFor="zip">Zip Code</FormLabel>
            <Input
              id="zip"
              mb={3}
              placeholder="Zip Code"
              {...form.register('zip', {
                pattern: {
                  message: 'Please use a valid zip code',
                  value: zipCodeRegExUs,
                },
                required: 'Please enter a zip code',
              })}
            />
            <FormErrorMessage>{form.formState.errors?.zip?.message}</FormErrorMessage>
          </FormControl>
        </VStack>
      );
  }

  return (
    <UI prevPage={ROUTES.createCitizenship + redirectAsParam}>
      <Flex px={[6, 20]} pt={['24px', '56px']} pb={[20, 200]} direction={'column'} gap={10} align="center">
        <PageHeader
          title="Residential Address"
          subtitle="Search for or manually enter your mailing address"
          step={2}
          steps={3}
        />

        <Box as="form" onSubmit={onSubmit} w={['100%', '350px', '350px']}>
          <FormControl display={addressState === 'SEARCH' ? 'block' : 'none'}>
            <Input
              style={{
                width: '100%',
              }}
              id="autocomplete"
              placeholder="Search your address..."
              type="text"
            />
            <TextBlock fontSize="12px" color="gray.300" padding="5px">
              * PO boxes or mail drops are not accepted
            </TextBlock>
          </FormControl>
          {content}
          <Flex justifyContent="center" my="12px">
            <Link
              onClick={() => {
                setAddressState(addressState === 'MANUAL' ? 'SEARCH' : 'MANUAL');
              }}>
              {addressState === 'MANUAL' ? 'Search for address' : 'Manually enter address'}
            </Link>
          </Flex>
          <Box>
            <Button
              tabIndex={addressState === 'DISPLAY' ? 0 : undefined}
              autoFocus={addressState === 'DISPLAY'}
              isDisabled={!address1 || !city || !state || !zip}
              type="submit"
              w="full"
              p={7}>
              Continue
            </Button>
          </Box>
        </Box>
      </Flex>
    </UI>
  );
};
